import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { fetchData } from 'utils';
import { useApplication, useClient, useLatestLoan } from 'hooks/webapi';
import dayjs from 'dayjs';

type Props = {
  children: ReactNode;
};

type BundleFeeResponse = {
  bundleFee: number;
  lastDateBeforeAutoProcessing?: string;
  parts: number;
  remainingNumberOfDays: number;
  singleFee: number;
};

type Bundle = {
  applyBundleExtension: () => Promise<void>;
  bundleFee: any;
  getBundleFee: () => Promise<void>;
  confirmBundleFee: () => Promise<void>;
  isBundleAvailable: boolean;
  isBundleAccepted: boolean;
  isBundleExtensionAvailable: boolean;
  bundleDays: {
    startDate?: string;
    transferDay: string;
    fullTransferDay: string;
    paymentDay: string;
    paymentDayAdditionalAmount: string;
  };
};

const BundleContext = createContext<Bundle>({} as Bundle);

const useBundle = (): Bundle => useContext(BundleContext);

const BundleProvider = ({ children }: Props): JSX.Element => {
  const [bundleFee, setBundleFee] = useState<
    BundleFeeResponse | undefined | null
  >();
  const { application } = useApplication();
  const { latestLoan } = useLatestLoan();
  const { client } = useClient();

  const getBundleFee = useCallback(async () => {
    try {
      const fee: BundleFeeResponse = await fetchData(
        '/client/application/bundle/info',
      );
      setBundleFee(fee);
    } catch {
      setBundleFee(null);
    }
  }, []);

  const confirmBundleFee = useCallback(async () => {
    await fetchData('/client/application/bundle/confirm', {
      method: 'put',
    });
  }, []);

  const applyBundleExtension = useCallback(async () => {
    await fetchData('/client/loans/latest/bundle-extension', {
      method: 'post',
    });
  }, []);

  const isValidApp = !application || application?.productNumber === '8';

  const isBundleAccepted = Boolean(latestLoan?.bundleAccepted);

  const isBundleAvailable =
    isBundleAccepted ||
    (isValidApp &&
      Boolean(
        latestLoan?.bundleOffered ||
          client?.productsEligibleForBundleOffer.includes('8'),
      ));

  useEffect(() => {
    if (bundleFee === undefined && client && isBundleAvailable) getBundleFee();
  }, [bundleFee, client, isBundleAvailable]);

  const isBundleExtensionAvailable = Boolean(
    latestLoan?.bundleExtensionPossible,
  );

  const startDate = application?.created;

  const defaultTransferDate = dayjs(startDate).add(7, 'days');
  const transferDate = bundleFee?.lastDateBeforeAutoProcessing
    ? dayjs(bundleFee?.lastDateBeforeAutoProcessing)
    : defaultTransferDate;

  const transferDay = transferDate.format('DD/MM/YYYY');
  const fullTransferDay = transferDate.format('YYYY-MM-DDTHH:mm:ss');

  const additionalPaymentDate =
    latestLoan?.dueDate && dayjs(latestLoan?.dueDate).format('DD/MM/YYYY');
  const repeatedLoanPaymentDate = dayjs(transferDate)
    .add(application?.term || 0, 'days')
    .format('DD/MM/YYYY');

  const bundleDays = {
    startDate,
    transferDay,
    fullTransferDay,
    paymentDayAdditionalAmount: dayjs(
      latestLoan?.lastDateForNewAmountWhenBundleNotAccepted || '',
    ).format('DD/MM/YYYY'),
    paymentDay: additionalPaymentDate || repeatedLoanPaymentDate,
  };

  const bundleContextValue = useMemo(
    () => ({
      applyBundleExtension,
      bundleFee,
      confirmBundleFee,
      getBundleFee,
      isBundleAvailable,
      isBundleAccepted,
      isBundleExtensionAvailable,
      bundleDays,
    }),
    [
      applyBundleExtension,
      bundleFee,
      confirmBundleFee,
      getBundleFee,
      isBundleAvailable,
      isBundleAccepted,
      isBundleExtensionAvailable,
      bundleDays,
    ],
  );

  return (
    <BundleContext.Provider value={bundleContextValue}>
      {children}
    </BundleContext.Provider>
  );
};

export { useBundle as default, BundleProvider };
